import { MARKETS, type Market } from '@ambernord/shared';
import { Button, Field, Input, Select, SlideOver, Textarea, useToast } from '@ambernord/ui';
import { Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { productName } from '../../lib/format.js';
import { type CustomerProfile, type ProductItem, errorKey, shipToLabel } from './types.js';

/**
 * Klienta RFQ forma — spoguļo POST /api/offers quoteBody Zod shēmu:
 * { customerId, market, shipToAddressId?, lines: [{productId, qty, unit?}], notes? }.
 * customerId + ship-to adreses nāk no GET /api/customers/me (klienta pats profils).
 */

interface LineDraft {
  productId: string;
  qty: string;
  unit: string;
}

export function RequestQuotePanel({
  open,
  onClose,
  onCreated,
  products,
  profile,
  profileMissing,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  products: ProductItem[];
  /** GET /api/customers/me; null kamēr ielādē vai ja nav pieejams. */
  profile: CustomerProfile | null;
  /** true tikai pie reāla 404 (klienta tenantam nav piesaistīts customers ieraksts). */
  profileMissing: boolean;
}) {
  const { t, i18n } = useTranslation('offers');
  const { toast } = useToast();
  const locale = i18n.language;

  const customerId = profile?.customer.id ?? null;
  const shipTo = profile?.shipTo ?? [];
  const firstProductId = products[0]?.id ?? '';
  const [market, setMarket] = useState<Market>('eu');
  const [shipToAddressId, setShipToAddressId] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([
    { productId: firstProductId, qty: '', unit: 'kg' },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function updateLine(index: number, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }

  function addLine() {
    setLines((prev) => [...prev, { productId: firstProductId, qty: '', unit: 'kg' }]);
  }

  function removeLine(index: number) {
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!customerId) return;
    for (const l of lines) {
      if (!l.productId) {
        setError(t('errors.selectProduct'));
        return;
      }
      const qty = Number(l.qty);
      if (!Number.isFinite(qty) || qty <= 0) {
        setError(t('errors.qtyPositive'));
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await api.post<{ offerId?: string; error?: string }>('/api/offers', {
        customerId,
        market,
        ...(shipToAddressId ? { shipToAddressId } : {}),
        lines: lines.map((l) => ({
          productId: l.productId,
          qty: Number(l.qty),
          ...(l.unit.trim() ? { unit: l.unit.trim() } : {}),
        })),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      if (!res.ok) {
        setError(t(errorKey(res.data.error)));
        return;
      }
      toast(t('toasts.quoteRequested'), 'success');
      setLines([{ productId: firstProductId, qty: '', unit: 'kg' }]);
      setShipToAddressId('');
      setNotes('');
      onCreated();
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SlideOver open={open} onClose={onClose} title={t('form.title')} className="max-w-lg">
      <form className="flex flex-1 flex-col gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
        {profileMissing ? (
          <p
            role="alert"
            className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
          >
            {t('form.profileMissing')}
          </p>
        ) : null}

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-sm font-semibold text-slate-900 dark:text-white">
            {t('form.linesTitle')}
          </legend>
          {lines.map((line, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rindām nav stabilas identitātes pirms saglabāšanas
            <div key={i} className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
              <div className="flex flex-col gap-2">
                <Field label={t('form.product')} htmlFor={`product-${String(i)}`}>
                  <Select
                    id={`product-${String(i)}`}
                    value={line.productId}
                    onChange={(e) => updateLine(i, { productId: e.target.value })}
                    required
                  >
                    {products.length === 0 ? (
                      <option value="">{t('form.noProducts')}</option>
                    ) : (
                      products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {productName(p.namesI18n, locale, p.sku)}
                        </option>
                      ))
                    )}
                  </Select>
                </Field>
                <div className="flex items-end gap-2">
                  <Field label={t('form.qty')} htmlFor={`qty-${String(i)}`} className="flex-1">
                    <Input
                      id={`qty-${String(i)}`}
                      type="number"
                      min={0}
                      step="any"
                      required
                      value={line.qty}
                      onChange={(e) => updateLine(i, { qty: e.target.value })}
                    />
                  </Field>
                  <Field label={t('form.unit')} htmlFor={`unit-${String(i)}`} className="w-24">
                    <Input
                      id={`unit-${String(i)}`}
                      value={line.unit}
                      onChange={(e) => updateLine(i, { unit: e.target.value })}
                    />
                  </Field>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mb-1 shrink-0"
                    aria-label={t('actions.removeLine')}
                    disabled={lines.length === 1}
                    onClick={() => removeLine(i)}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="self-start"
            onClick={addLine}
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t('actions.addLine')}
          </Button>
        </fieldset>

        <Field label={t('form.market')} htmlFor="rfq-market">
          <Select
            id="rfq-market"
            value={market}
            onChange={(e) => setMarket(e.target.value as Market)}
          >
            {MARKETS.map((m) => (
              <option key={m} value={m}>
                {t(`form.markets.${m}`)}
              </option>
            ))}
          </Select>
        </Field>

        {shipTo.length > 0 ? (
          <Field label={t('form.shipTo')} htmlFor="rfq-ship-to" hint={t('form.shipToHint')}>
            <Select
              id="rfq-ship-to"
              value={shipToAddressId}
              onChange={(e) => setShipToAddressId(e.target.value)}
            >
              <option value="">{t('form.shipToNone')}</option>
              {shipTo.map((a) => (
                <option key={a.id} value={a.id}>
                  {shipToLabel(a)}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        <Field label={t('form.notes')} htmlFor="rfq-notes" hint={t('form.notesHint')}>
          <Textarea
            id="rfq-notes"
            rows={3}
            maxLength={2000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>

        {error ? (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        ) : null}

        <div className="mt-auto flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={submitting || products.length === 0 || customerId === null}
          >
            {submitting ? t('loading') : t('actions.submit')}
          </Button>
        </div>
      </form>
    </SlideOver>
  );
}
